import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { IdCard, Plus } from "shared/assets/icons";
import { Button, Card, Loader, Message, PassportTeaser } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingUserDocuments } from "Bands/utils";

const passportCopy = copy.userPassport;

export const Passports: FC = () => {
  const { passports, setShowCreateModal, setShowEditModal, setShowDeleteModal, getItemActions } =
    useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.passport,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.passport,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingPassports } = useFetchingUserDocuments();

  const content = useMemo(() => {
    if (fetchingPassports) {
      return <Loader />;
    }

    if (!passports?.length) {
      return <Message message={passportCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {passports?.map(passport => (
          <PassportTeaser key={passport.id} {...passport} actions={getActions(passport.id)} />
        ))}
      </GridContainer>
    );
  }, [passports, fetchingPassports, getActions]);

  return (
    <Card icon={IdCard} title={passportCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.passport)}
          data-testid="add_passport_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{passportCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
